import React, { useState, useEffect } from 'react';
import { Printer, Download, Calendar, Clock } from 'lucide-react';
import axios from 'axios';

const PrintRoutine = () => {
  const [classes, setClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ 
    course_id: '', 
    semester_id: '', 
    section_id: '' 
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Get current academic year (e.g., 2025-2026)
  const getCurrentAcademicYear = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-11
    // Academic year typically starts in July (month 6)
    if (currentMonth >= 6) {
      return `${currentYear}-${currentYear + 1}`;
    } else {
      return `${currentYear - 1}-${currentYear}`;
    }
  };

  useEffect(() => {
    fetchClasses();
    fetchCourses();
    fetchSemesters();
    fetchSections();
    fetchSubjects();
    fetchStaff();
  }, []);

  const fetchClasses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/routine/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSemesters = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/semesters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSemesters(response.data);
    } catch (error) {
      console.error('Error fetching semesters:', error);
    }
  };

  const fetchSections = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/sections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSections(response.data);
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/subjects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/staff/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaff(response.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  };

  // Filter semesters based on selected course
  const getFilteredSemesters = () => {
    if (!filters.course_id) return [];
    return semesters.filter(sem => sem.course_id === filters.course_id);
  };

  // Handle course change
  const handleCourseChange = (courseId) => {
    setFilters({ 
      ...filters, 
      course_id: courseId,
      semester_id: '' 
    });
  };

  // Get filtered classes
  const getFilteredClasses = () => {
    return classes.filter(cls => {
      if (filters.course_id && cls.course_id !== filters.course_id) return false;
      if (filters.semester_id && cls.semester_id !== filters.semester_id) return false;
      if (filters.section_id && cls.section_id !== filters.section_id) return false;
      return cls.is_active;
    });
  };

  // Generate dynamic time slots
  const generateTimeSlots = () => {
    const filteredClasses = getFilteredClasses();
    if (filteredClasses.length === 0) return [];
    
    const hours = new Set();
    filteredClasses.forEach(cls => {
      const hour = cls.start_time.split(':')[0];
      hours.add(hour);
    });
    
    return Array.from(hours).sort().map(hour => `${hour}:00`);
  };

  // Get classes for specific day and time slot
  const getClassForSlot = (day, timeSlot) => {
    const filteredClasses = getFilteredClasses();
    return filteredClasses.filter(cls => {
      if (cls.day !== day) return false;
      const classHour = cls.start_time.split(':')[0];
      const slotHour = timeSlot.split(':')[0];
      return classHour === slotHour;
    });
  };

  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject ? subject.name : 'N/A';
  };

  const getSubjectCode = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject ? subject.code : '';
  };

  const getTeacherName = (teacherId) => {
    const teacher = staff.find(t => t.id === teacherId);
    return teacher ? (teacher.full_name || teacher.name || 'N/A') : 'N/A';
  };

  const getCourseName = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course ? course.name : '';
  };

  const getSemesterName = (semId) => {
    const sem = semesters.find(s => s.id === semId);
    return sem ? sem.name : '';
  };

  const getSectionName = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    return section ? section.name : '';
  };

  const handlePrint = () => {
    window.print();
  };

  const filteredClasses = getFilteredClasses();
  const timeSlots = generateTimeSlots();
  const showRoutine = filters.course_id && filteredClasses.length > 0;

  return (
    <div className="space-y-6">
      {/* Header - Hidden on Print */}
      <div className="flex justify-between items-center print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Print Routine</h1>
          <p className="text-sm text-gray-500 mt-1">Print class routine/timetable</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handlePrint}
            disabled={!showRoutine}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              showRoutine 
                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Printer className="h-4 w-4" />
            Print
          </button>
        </div>
      </div>

      {/* Filters - Hidden on Print */}
      <div className="bg-white rounded-lg shadow p-4 print:hidden">
        <h3 className="text-lg font-semibold mb-4">Select Class</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Course Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Course *</label>
            <select 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.course_id}
              onChange={(e) => handleCourseChange(e.target.value)}
            >
              <option value="">Select Course</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>

          {/* Semester Dropdown (Connected to Course) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Semester
              {filters.course_id && getFilteredSemesters().length === 0 && (
                <span className="text-xs text-orange-600 ml-2">(No semesters)</span>
              )}
            </label>
            <select
              disabled={!filters.course_id}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 ${
                !filters.course_id ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              value={filters.semester_id}
              onChange={(e) => setFilters({ ...filters, semester_id: e.target.value })}
            >
              <option value="">{!filters.course_id ? 'Select Course First' : 'All Semesters'}</option>
              {getFilteredSemesters().map(sem => (
                <option key={sem.id} value={sem.id}>{sem.name}</option>
              ))}
            </select>
          </div>

          {/* Section Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Section</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.section_id}
              onChange={(e) => setFilters({ ...filters, section_id: e.target.value })}
            >
              <option value="">All Sections</option>
              {sections.map(section => (
                <option key={section.id} value={section.id}>{section.name}</option>
              ))}
            </select>
          </div>

          {/* Info */}
          <div className="flex items-end">
            <div className="w-full">
              {showRoutine && (
                <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-center">
                  <p className="text-xs text-green-700 font-medium">
                    {filteredClasses.length} Classes
                  </p>
                  <p className="text-xs text-green-600">Ready to print</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Print Preview Area */}
      <div className="bg-white rounded-lg shadow p-8">
        {/* Header - Shows on Print */}
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-gray-900 print:text-4xl">CLASS ROUTINE</h2>
          <p className="text-gray-600 mt-2 text-lg">Academic Year {getCurrentAcademicYear()}</p>
          {showRoutine && (
            <div className="mt-4 space-y-1">
              <p className="text-sm font-semibold text-gray-700">
                Course: {getCourseName(filters.course_id)}
              </p>
              {filters.semester_id && (
                <p className="text-sm text-gray-600">
                  Semester: {getSemesterName(filters.semester_id)}
                </p>
              )}
              {filters.section_id && (
                <p className="text-sm text-gray-600">
                  Section: {getSectionName(filters.section_id)}
                </p>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="py-12 text-center text-gray-500">Loading...</div>
        ) : !showRoutine ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg py-12">
            <div className="text-center">
              <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">Select Course to Load Routine</p>
              <p className="text-sm text-gray-400 mt-2">Choose course, semester and section from filters above</p>
            </div>
          </div>
        ) : (
          <div>
            {/* Timetable */}
            <div className="border-2 border-gray-900 rounded-lg overflow-x-auto">
              <table className="min-w-full border-collapse">
                <thead>
                  <tr className="bg-gray-900">
                    <th className="px-3 py-3 text-left text-sm font-bold text-white border border-gray-700">
                      Time
                    </th>
                    {daysOfWeek.map(day => (
                      <th key={day} className="px-3 py-3 text-center text-sm font-bold text-white border border-gray-700">
                        {day}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {timeSlots.map((timeSlot, index) => (
                    <tr key={timeSlot} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-4 text-sm font-semibold text-gray-900 border border-gray-300">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-gray-500" />
                          {timeSlot}
                        </div>
                      </td>
                      {daysOfWeek.map(day => {
                        const dayClasses = getClassForSlot(day, timeSlot);
                        return (
                          <td key={`${day}-${timeSlot}`} className="px-2 py-2 border border-gray-300 align-top">
                            {dayClasses.length > 0 ? (
                              <div className="space-y-2">
                                {dayClasses.map((cls, idx) => (
                                  <div key={idx} className="text-xs">
                                    <div className="font-bold text-gray-900">
                                      {getSubjectName(cls.subject_id)}
                                    </div>
                                    <div className="text-gray-600 mt-0.5">
                                      {cls.start_time} - {cls.end_time}
                                    </div>
                                    <div className="text-gray-500 mt-0.5">
                                      {getTeacherName(cls.teacher_id)}
                                    </div>
                                    {getSubjectCode(cls.subject_id) && (
                                      <div className="text-gray-400 text-[10px] mt-0.5">
                                        ({getSubjectCode(cls.subject_id)})
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center text-gray-300 py-2">
                                —
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer - Shows on Print */}
            <div className="mt-8 pt-6 border-t">
              <div className="flex justify-between items-end">
                <div>
                  <p className="text-xs text-gray-500 mb-2">Principal's Signature</p>
                  <div className="border-b-2 border-gray-400 w-48"></div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-2">Date</p>
                  <div className="border-b-2 border-gray-400 w-32"></div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Print Styles */}
      <style jsx global>{`
        @media print {
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          .print\:hidden { display: none !important; }
          @page { size: landscape; margin: 1cm; }
        }
      `}</style>
    </div>
  );
};

export default PrintRoutine;
